
import { Component, Emit, Inject, InjectReactive, Vue, Watch } from 'vue-property-decorator';
import type { VinculoPontoUnidade } from '../types-registro-ponto';
import RegistroPontoNovo from '../RegistroPontoNovo.vue';
import _ from 'lodash';
import SnackbarUtils from '@tekvue/assets/utils/SnackbarUtils';
import ProgressBar from '@/components/ProgressBar.vue';
import CameraController from '@/assets/ts/controller/CameraController';
import { FaceDetection } from 'face-api.js';
import OfflineController from '@/assets/ts/controller/OfflineController';
import registroPontoMessages from '@/assets/ts/messages/registroPontoMessages';
import ReconhecimentoFacialController from '@/assets/ts/controller/ReconhecimentoFacialController';
import ReconhecimentoFacialUtils from '@/assets/ts/utils/ReconhecimentoFacialUtils';
import CommonRequests from '@/assets/ts/controller/CommonRequests';
import CancelError from '../CancelError';

enum AlertType {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
}

@Component({
  components: {
    ProgressBar,
  },
})
export default class RegistroPontoRecFacial extends Vue {
  //#region [ CONSTANTS ]
  readonly SCREEN_TIMEOUT = 120_000 as const; // 2 minutes
  readonly VIEW_ID = '#camera-area__view' as const;
  readonly SENSOR_ID = '#camera-area__sensor' as const;
  readonly VISUALIZER_ID = '#camera-area__visualizer' as const;
  readonly FACE_ID = '#camera-area__face' as const;
  //#endregion



  //#region [ CONTEXT ]
  @Inject() findVinculo!: typeof RegistroPontoNovo.prototype.findVinculo;
  @InjectReactive() vinculos!: VinculoPontoUnidade[];
  //#endregion

  //#region [ STORE ]
  get isOffline():boolean {
    return this.$store.state.isOffline;
  }
  //#endregion



  //#region [ DATA ]
  screenTimeout: number | null = null;
  showCamera: boolean = true;
  detectionPaused: boolean = false;
  detectionTimeout: number | null = null;
  timerRecFacial: number = 1000; // Default to 1 second

  faceDetectionTime: number = null;
  faceProgress: number = 0;

  message: string = '';
  alertType: AlertType = AlertType.INFO;
  alertTimer: number | null = null;

  promise: {
    resolve: (value: [VinculoPontoUnidade, string]) => void;
    reject: (reason?: any) => void;
  } | null = null;

  camera: CameraController = new CameraController(
    this.VIEW_ID,
    this.SENSOR_ID,
    this.VISUALIZER_ID,
    this.FACE_ID,
  );
  //#endregion



  //#region [ COMPUTED ]
  get progressBar(): ProgressBar {
    return this.$refs.progressBar as ProgressBar || null;
  }

  get vinculosHashes(): string | false {
    return ReconhecimentoFacialUtils.tratarVinculosParaRecFacial(this.vinculos);
  }
  //#endregion



  //#region [ EVENTS ]
  @Emit('cancel')
  emitCancel(): void {
    if (this.promise) {
      this.promise.reject(new CancelError());
      this.promise = null; // Reset after rejecting
    }

    this.end();
  }
  //#endregion



  //#region [ LIFECYCLE ]
  created(): void {
    this.getTimeRecFacial();
  }

  beforeDestroy(): void {
    this.end();
  }
  //#endregion



  //#region [ WATCHERS ]
  @Watch('isOffline')
  onOfflineChange(isOffline: boolean): void {
    if (!this.verifyOnline()) {
      this.emitCancel();
    }
  }
  //#endregion



  //#region [ METHODS ]
  scan(): Promise<[VinculoPontoUnidade, string]> {
    return new Promise(async (resolve, reject) => {
      this.promise = {
        resolve,
        reject,
      };
      // Set a timeout to auto-cancel after 30 seconds if not resolved
      this.screenTimeout = window.setTimeout(() => {
        this.emitCancel();
      }, this.SCREEN_TIMEOUT);

      try {
        this.progressBar.start(this.SCREEN_TIMEOUT);
        await this.startCamera();
        await this.initFacialRecognition();
        this.resumeDetection();
      } catch (err) {
        reject(err);
      }
    });
  }

  end(): void {
    this.clearScreenTimeout();
    this.stopCamera();
    this.cancelFaceDetection();
    this.resetFaceBox();
  }

  startCamera(): Promise<MediaProvider> {
    this.showCamera = true;
    return this.camera.initTransmitionCameraDevice();
  }

  stopCamera(): void {
    CameraController.stopUsingCameraDevice(this.VIEW_ID);
  }

  clearScreenTimeout(): void {
    if (this.screenTimeout) {
      window.clearTimeout(this.screenTimeout);
      this.screenTimeout = null;
    }
  }

  async initFacialRecognition(): Promise<void> {
    try {
      await CameraController.initLoadFaceModules();
      this.createFaceDetectionHandler();
      this.camera.faceDetectLive();
    } catch (error) {
      const msg = error as string;
      console.error(msg);
      SnackbarUtils.showMessage(msg, SnackbarUtils.TYPE_ERROR, 7000);
    }
  }

  createFaceDetectionHandler(): void {
    this.camera.onDetect('detections', this.onFaceDetection);
  }

  async onFaceDetection (detections: FaceDetection[]): Promise<void> {
    if (this.detectionPaused || !this.vinculosHashes) {
      return;
    }

    if (!detections || !detections.length) {
      this.showMessage(
        registroPontoMessages.nenhumRostoDetectado,
        AlertType.WARNING,
      );

      this.resetFaceBox();
      return;
    }

    if (detections.length > 1) {
      this.showMessage(
        registroPontoMessages.multipleFacesDetectedLive,
        AlertType.WARNING,
      );

      this.resetFaceBox();
      return;
    }

    if (detections[0].score < 0.85) {
      this.showMessage(
        registroPontoMessages.baixoScoreFace,
        AlertType.WARNING,
      );

      this.resetFaceBox();
      return;
    }

    this.setFaceBox();
    this.calculateFaceProgress();

    if (this.faceProgress < 100) {
      return;
    }

    this.resetFaceBox();

    const base64Image = await this.camera.takePicture();
    this.pauseDetection();

    this.showMessage(
      registroPontoMessages.reconhecimentoOngoing,
      AlertType.INFO,
      5000,
    );

    const nrVinculo = await ReconhecimentoFacialController.reconhecimentoFacial(base64Image, this.vinculosHashes);

    if (!nrVinculo) {
      this.showMessage(
        registroPontoMessages.noFacesMatches,
        AlertType.ERROR,
        3000,
      );

      this.pauseDetection(3000);

      this.camera.resume();
      return;
    }

    const vinculo = this.findVinculo(nrVinculo.toString());

    this.solvePromise(vinculo, base64Image);
  }

  solvePromise(vinculo: VinculoPontoUnidade, base64Image: string): void {
    if (this.promise) {
      this.promise.resolve([vinculo, base64Image]);
      this.promise = null;
    }

    this.end();
  }

  verifyOnline(): boolean {
    if (OfflineController.checkIsOffline()) {
      SnackbarUtils.showMessage(
        registroPontoMessages.reconhecimentoFacialNaoDisponivelOffline,
        SnackbarUtils.TYPE_ERROR,
        5000,
      );
      return false;
    }

    return true;
  }

  cancelFaceDetection(): void {
    this.camera.cancelFaceDetection();
    this.camera.offDetect('detections', this.onFaceDetection);
  }

  showMessage(message: string, type: AlertType, duration: number = 250): void {
    this.message = message;
    this.alertType = type;

    if (this.alertTimer) {
      window.clearTimeout(this.alertTimer);
    }

    this.alertTimer = window.setTimeout(() => {
      this.message = '';
      this.alertTimer = null;
    }, duration); // Clear message after 5 seconds
  }

  pauseDetection(duration?: number): void {
    this.detectionPaused = true;

    if (this.detectionTimeout) {
      window.clearTimeout(this.detectionTimeout);
      this.detectionTimeout = null;
    }

    if (duration) {
      this.detectionTimeout = window.setTimeout(() => {
        this.detectionPaused = false;
        this.detectionTimeout = null;
      }, duration);
    }
  }

  resumeDetection(): void {
    this.detectionPaused = false;

    if (this.detectionTimeout) {
      window.clearTimeout(this.detectionTimeout);
      this.detectionTimeout = null;
    }
  }

  async getTimeRecFacial(): Promise<void> {
    try {
      const valorReferencia = await CommonRequests.getValorReferencia('TIMER_REC_FACIAL');

      let time: number;

      if (!isNaN(parseFloat(valorReferencia))) {
        const numValorReferencia = parseFloat(valorReferencia);

        time = Math.min(Math.max(numValorReferencia * 1000, 1000), 3000);
      } else {
        time = 1000;
      }

      this.timerRecFacial = time;
    } catch (error) {
      //
    }
  }

  setFaceBox(): void {
    if (!this.faceDetectionTime) {
      this.faceDetectionTime = Date.now();
    }
  }

  resetFaceBox(): void {
    this.faceDetectionTime = null;
    this.faceProgress = 0;
  }

  calculateFaceProgress(): void {
    const percentage = ((Date.now() - this.faceDetectionTime) / this.timerRecFacial) * 100;
    this.faceProgress = Math.min(percentage, 100);
  }
  //#endregion
}
